/**
 * THE SUBSCRIPTION FORM — the expert's side (EXPERT-ROOMS.md §14b, owner 2026-10-28). What a workspace fills in when it
 * subscribes: an agent DRAFTS it from the expert's needs; the owner shapes it by PROMPT only (no hand edits); VALIDATE
 * runs the contract checks (every platform adapter's parameter bound, no secret asked) + a dry run with test answers.
 * Promote to Production waits until the release's form passed. An expert that needs nothing has no form (one-click
 * subscribe). Backend: PUT /networks/{id} ``{expertReleases: {action: 'form' | 'formDraft' {ask} | 'formValidate' |
 * 'formPrivilege' {adapter, param, value|null} | 'formUpdate' {use}}}``. A newer form its PLUGIN delivered while the owner had
 * changed theirs waits here (P3): Use it (their privileged picks carry over) or Keep mine. A PRIVILEGED adapter value (e.g. read access to data, admin) is never
 * the generator's call — the owner sets it here, explicitly (P2, 2026-11-03).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { SubscriptionFormFields } from '@stepflow/ui-shared/components/SubscriptionForm.jsx';
import { confirmDialog } from '@stepflow/ui-shared/components/DialogHost.jsx';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';
const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';

export const FORM_STATUS = {
  none: { label: 'Not needed — subscribers join in one click', tone: 'bg-gray-100 text-gray-600' },
  missing: { label: 'Needed — draft one', tone: 'bg-amber-50 text-amber-800' },
  unchecked: { label: 'Not validated', tone: 'bg-amber-50 text-amber-800' },
  failed: { label: 'Validation failed', tone: 'bg-rose-50 text-rose-700' },
  valid: { label: 'Valid', tone: 'bg-emerald-50 text-emerald-700' },
};

async function call(networkId, req) {
  const r = await fetch(`${API_BASE_URL}/networks/${encodeURIComponent(networkId)}`, {
    method: 'PUT', headers: { Authorization: `Bearer ${getRestApiBearerToken()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ expertReleases: req }),
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.message || `HTTP ${r.status}`);
  return body.subscriptionForm || body.expertReleases?.subscriptionForm || {};
}

/** PURE — the privileged choices the form's adapters offer: ``[{adapter, param, label, values, set, setBy}]``. */
export function privilegedChoices(data) {
  const out = [];
  const form = data?.form || {};
  for (const a of (form.adapters || [])) {
    const params = data?.adapters?.[a.id]?.params || {};
    for (const [p, spec] of Object.entries(params)) {
      if (!(spec?.privileged || []).length) continue;
      const rec = form.privileged?.[`${a.id}.${p}`];
      out.push({ adapter: a.id, param: p, label: spec.label || p, values: spec.privileged, labels: spec.optionLabels || {},
        current: (a.fixed || {})[p] ?? spec.default ?? '', set: rec?.value || '' });
    }
  }
  return out;
}

export default function ExpertSubscriptionFormView({ network, canEdit = false }) {
  const networkId = network?.networkId;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [ask, setAsk] = useState('');
  const [busy, setBusy] = useState('');
  const run = useCallback(async (key, req) => {
    setBusy(key); setErr('');
    try { setData(await call(networkId, req)); return true; } catch (e) { setErr(e.message); return false; } finally { setBusy(''); }
  }, [networkId]);
  useEffect(() => { if (networkId) run('load', { action: 'form' }); }, [networkId, run]);

  if (!data) return <div className="p-4 text-xs text-gray-500" data-testid="subscription-form-loading">{err || 'Loading…'}</div>;
  const form = data.form || {};
  const st = FORM_STATUS[data.status] || FORM_STATUS.missing;
  const checks = data.check?.checks || [];
  const hasForm = !!((form.fields || []).length || (form.adapters || []).length);
  return (
    <div className="p-4 text-[12px]" data-testid="expert-subscription-form">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900">Subscription form</h3>
          <p className="mt-0.5 max-w-prose text-xs text-gray-500">
            What a workspace answers when it subscribes. The platform sets things up from the answers — the expert never
            sees them. Shape it by asking below; validate it before Production.
          </p>
        </div>
        <span className={`shrink-0 rounded px-1.5 py-px text-[11px] font-medium ${st.tone}`} data-testid="subscription-form-status">{st.label}</span>
      </div>
      {(data.needs || []).length ? (
        <p className="mt-2 text-[11px] text-gray-600" data-testid="subscription-form-needs">
          Its needs call for: {(data.needs || []).map((a) => data.adapters?.[a]?.label || a).join(' · ')}
        </p>
      ) : null}

      {canEdit && data.update?.form ? (
        <div className="mt-3 max-w-md rounded-md border border-indigo-200 bg-indigo-50/40 p-3" data-testid="subscription-form-update">
          <p className="text-xs font-medium text-indigo-900">The plugin has a newer form{data.update.version ? ` (v${data.update.version})` : ''}</p>
          <p className="mt-0.5 text-[11px] text-indigo-800">
            You changed this form, so yours was kept. The newer one asks {(data.update.resolved?.fields || data.update.form.fields || []).length} question(s)
            {Array.isArray(data.update.mustAnswer) ? `, ${data.update.mustAnswer.length} to answer` : ''}. Your “more access” choices carry over.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button type="button" disabled={!!busy} data-testid="subscription-form-update-use"
              className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50`}
              onClick={async () => {
                if (!(await confirmDialog({ title: 'Use the newer form?', confirmLabel: 'Use it',
                  message: 'It replaces your version of the form (your changes go). Validate it again before Production.' }))) return;
                run('update', { action: 'formUpdate', use: true });
              }}>Use it…</button>
            <button type="button" disabled={!!busy} data-testid="subscription-form-update-keep"
              className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50`}
              onClick={() => run('update', { action: 'formUpdate', use: false })}>Keep mine</button>
          </div>
        </div>
      ) : null}

      {hasForm ? (
        <div className="mt-3 max-w-md rounded-md border border-gray-200 p-3" data-testid="subscription-form-preview">
          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Preview — what subscribers see</p>
          {form.title ? <p className="mt-1 text-xs font-semibold text-gray-800">{form.title}</p> : null}
          {/* RESOLVED = exactly what subscribers get (the adapters' choices, labels and helpers — P2) */}
          <div className="mt-2"><SubscriptionFormFields form={(data.resolved?.fields || []).length ? data.resolved : form}
            values={data.example || {}} readOnly testid="subscription-form-preview-fields" /></div>
          {Array.isArray(data.mustAnswer) ? (
            <p className={`mt-2 text-[11px] ${data.mustAnswer.length > 3 ? 'text-amber-700' : 'text-gray-500'}`}
              data-testid="subscription-form-must-answer">
              {data.mustAnswer.length === 0 ? 'Nothing must be answered — every question has a default.'
                : `${data.mustAnswer.length} question${data.mustAnswer.length === 1 ? '' : 's'} must be answered`
                  + (data.mustAnswer.length > 3 ? ' — aim for 3 or fewer: give defaults or let the setup find them.' : '.')}
            </p>
          ) : null}
        </div>
      ) : null}

      {canEdit ? (
        <div className="mt-3 max-w-md space-y-2">
          <textarea value={ask} onChange={(e) => setAsk(e.target.value)} rows={2} maxLength={2000}
            placeholder={hasForm ? 'Ask for a change — e.g. “also ask for the cost center; default the region to eu-west-1”' : 'Optional — what should it ask? (blank = draft from the expert’s needs)'}
            data-testid="subscription-form-ask" className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" />
          <div className="flex items-center gap-2">
            <button type="button" disabled={!!busy} data-testid="subscription-form-draft"
              onClick={async () => { if (await run('draft', { action: 'formDraft', ask })) setAsk(''); }}
              className={`${btn} inline-flex items-center gap-1 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50`}>
              {busy === 'draft' ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
              {busy === 'draft' ? 'Drafting…' : hasForm ? 'Revise' : 'Draft the form'}
            </button>
            <button type="button" disabled={!!busy || !hasForm} data-testid="subscription-form-validate"
              onClick={() => run('validate', { action: 'formValidate' })}
              className={`${btn} inline-flex items-center gap-1 border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50`}>
              {busy === 'validate' ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : null}
              {busy === 'validate' ? 'Validating…' : 'Validate'}
            </button>
          </div>
        </div>
      ) : null}

      {canEdit && privilegedChoices(data).length ? (
        <div className="mt-3 max-w-md rounded-md border border-amber-200 bg-amber-50/40 p-3" data-testid="subscription-form-privileged">
          <p className="text-[11px] font-medium uppercase tracking-wide text-amber-700">More access — only you decide</p>
          {privilegedChoices(data).map((c) => (
            <div key={`${c.adapter}.${c.param}`} className="mt-1.5 flex flex-wrap items-center gap-2 text-xs"
              data-testid={`subscription-form-privileged-${c.param}`}>
              <span className="text-gray-800">{c.label}: <b>{c.labels[c.current] || c.current || '—'}</b>{c.set ? ' (set by the owner)' : ''}</span>
              {c.set ? (
                <button type="button" disabled={!!busy} className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50`}
                  data-testid={`subscription-form-privileged-clear-${c.param}`}
                  onClick={() => run('priv', { action: 'formPrivilege', adapter: c.adapter, param: c.param, value: null })}>
                  Back to the default
                </button>
              ) : c.values.map((v) => (
                <button key={v} type="button" disabled={!!busy} className={`${btn} border border-amber-300 text-amber-800 hover:bg-amber-100`}
                  data-testid={`subscription-form-privileged-set-${c.param}-${v}`}
                  onClick={async () => {
                    if (!(await confirmDialog({ title: `Allow ${c.labels[v] || v}?`, danger: true, confirmLabel: 'Allow',
                      message: `Every workspace that subscribes gives this expert ${c.labels[v] || v}. Validate the form again afterwards.` }))) return;
                    run('priv', { action: 'formPrivilege', adapter: c.adapter, param: c.param, value: v });
                  }}>
                  Allow {c.labels[v] || v}…
                </button>
              ))}
            </div>
          ))}
        </div>
      ) : null}

      {checks.length && data.status !== 'unchecked' ? (
        <ul className="mt-3 max-w-md space-y-1" data-testid="subscription-form-checks">
          {checks.map((c, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs" data-testid={`subscription-form-check-${c.ok ? 'ok' : 'fail'}`}>
              {c.ok ? <CheckCircle2 className="mt-px h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
                : <XCircle className="mt-px h-3.5 w-3.5 shrink-0 text-rose-600" aria-hidden />}
              <span className="text-gray-800">{c.check}{c.detail ? <span className="text-gray-500"> — {c.detail}</span> : null}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {data.status === 'valid' ? (
        <p className="mt-2 text-[11px] text-gray-500">Cut a release to ship it — Production takes a release whose form passed.</p>
      ) : null}
      {err ? <p className="mt-2 text-[11px] text-rose-600" data-testid="subscription-form-error">{err}</p> : null}
    </div>
  );
}
